import React, { useEffect, useState, useRef } from 'react';
import { initializeModal } from '../../utils/ModalUtils';
import { refineBlueprint, updateTemplateBlueprint, saveGeneratedTemplate } from '../../services/StrategyTemplatesService';
import { processError } from '../../services/ServiceUtils';
import BlueprintGateResults from './BlueprintGateResults';

// Fase 2d.4 — UI admin de REFINO de um template por IA. Recebe o template (props.data), pede uma META em
// pt-BR, a IA devolve o blueprint melhorado (mudança cirúrgica) e o backend já roda os gates. O admin revisa
// e escolhe: ATUALIZAR no lugar (só templates NÃO-sistema) ou SALVAR COMO NOVO (fork — sempre disponível,
// única via para os starters do sistema). Fecha o loop com a avaliação semanal. Ver project_fase2d_blueprint_design.

const DIFFICULTIES = ['Fácil', 'Média', 'Difícil'];
const GOAL_EXAMPLES = [
    { label: 'Saída mais rápida', text: 'Deixar a saída mais rápida: vender assim que o RSI passar de 55, em vez de esperar mais.' },
    { label: 'Filtro de tendência', text: 'Adicionar filtro de tendência: só comprar se o preço estiver acima da EMA de 50.' },
    { label: 'Reduzir churn', text: 'Reduzir o churn: exigir que o RSI já esteja subindo (repique) antes de comprar.' },
];

function RefineStrategyModal(props) {
    const btnClose = useRef('');
    const [goal, setGoal] = useState('');
    const [interval, setIntervalCfg] = useState('1h');
    const [walletPercent, setWalletPercent] = useState(20);
    const [refining, setRefining] = useState(false);
    const [cand, setCand] = useState(null);        // { blueprint, ok, gates, source, _servedBy }
    const [error, setError] = useState('');
    const [name, setName] = useState('');
    const [difficulty, setDifficulty] = useState('Média');
    const [saving, setSaving] = useState(false);

    const tpl = props.data;
    const isSystem = tpl && tpl.isSystem;

    useEffect(() => { initializeModal('modalRefineStrategy', reset); }, []);

    // Reseta quando abre para outro template.
    useEffect(() => {
        setGoal(''); setCand(null); setError(''); setSaving(false);
        setName(tpl ? `${tpl.name} (refinado)` : '');
        setDifficulty((tpl && tpl.difficulty) || 'Média');
    }, [tpl]);

    function reset() {
        setGoal(''); setCand(null); setError(''); setSaving(false); setIntervalCfg('1h'); setWalletPercent(20);
    }

    function onRefine() {
        if (!tpl) return;
        if (!goal.trim()) { setError('Descreva a melhoria desejada.'); return; }
        setRefining(true); setError(''); setCand(null);
        refineBlueprint({ templateId: tpl.id, goal: goal.trim(), config: { interval, walletPercent: Number(walletPercent) || 20 } })
            .then(res => { setRefining(false); setCand(res); })
            .catch(err => { setRefining(false); setError(processError(err)); });
    }

    function onUpdateInPlace() {
        if (!cand || !cand.ok || !tpl) return;
        setSaving(true); setError('');
        updateTemplateBlueprint({ templateId: tpl.id, blueprint: cand.blueprint })
            .then(() => { setSaving(false); if (btnClose.current) btnClose.current.click(); if (props.onSaved) props.onSaved('Template atualizado com a versão refinada!'); })
            .catch(err => { setSaving(false); const d = err && err.response && err.response.data; setError(d && d.error ? d.error : processError(err)); });
    }

    function onSaveAsNew() {
        if (!cand || !cand.ok || !name.trim()) return;
        setSaving(true); setError('');
        saveGeneratedTemplate({ blueprint: cand.blueprint, name: name.trim(), difficulty })
            .then(() => { setSaving(false); if (btnClose.current) btnClose.current.click(); if (props.onSaved) props.onSaved('Versão refinada salva como um novo template!'); })
            .catch(err => { setSaving(false); const d = err && err.response && err.response.data; setError(d && d.error ? d.error : processError(err)); });
    }

    return (
        <div className="modal fade" id="modalRefineStrategy" tabIndex="-1" role="dialog" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <p className="modal-title">🔧 Refinar com IA {tpl ? `— ${tpl.name}` : ''}</p>
                        <button ref={btnClose} type="button" className="btn-close" data-bs-dismiss="modal" aria-label="close"></button>
                    </div>
                    <div className="modal-body">

                        {!tpl ? <div className="text-muted text-center py-3">Selecione um template.</div> : (
                        <React.Fragment>
                            <div className="alert alert-info py-2 small mb-3">
                                A IA parte do desenho ATUAL desta estratégia e aplica só a melhoria que você pedir, sem quebrar as travas de segurança. Depois passa pelos mesmos gates (segurança + simulação).
                                {isSystem && <div className="mt-1"><strong>Este é um template do sistema</strong> — não dá para sobrescrevê-lo; a versão refinada será salva como um <strong>novo template</strong>.</div>}
                            </div>

                            <div className="mb-2">
                                <label htmlFor="refGoal">O que melhorar?</label>
                                <textarea className="form-control" id="refGoal" rows="3" value={goal}
                                    placeholder={GOAL_EXAMPLES[0].text} onChange={e => setGoal(e.target.value)} />
                                <div className="mt-2 d-flex align-items-center flex-wrap gap-2">
                                    <span className="text-muted small">Começar de um exemplo:</span>
                                    {GOAL_EXAMPLES.map((ex, i) => (
                                        <button key={i} type="button" className="btn btn-sm btn-outline-primary"
                                            onClick={() => setGoal(ex.text)}>{ex.label}</button>
                                    ))}
                                </div>
                            </div>

                            <div className="row mb-3">
                                <div className="col-6 col-md-3">
                                    <label htmlFor="refInterval">Tempo gráfico</label>
                                    <input className="form-control form-control-sm" id="refInterval" value={interval}
                                        onChange={e => setIntervalCfg(e.target.value)} placeholder="1h" />
                                </div>
                                <div className="col-6 col-md-3">
                                    <label htmlFor="refWallet">% da carteira</label>
                                    <input className="form-control form-control-sm" id="refWallet" type="number" value={walletPercent}
                                        onChange={e => setWalletPercent(e.target.value)} />
                                </div>
                                <div className="col-12 col-md-6 d-flex align-items-end">
                                    <button type="button" className="btn btn-primary w-100" onClick={onRefine} disabled={refining}>
                                        {refining ? 'Refinando e validando…' : (cand ? 'Refinar de novo' : 'Refinar com IA')}
                                    </button>
                                </div>
                            </div>

                            {error && <div className="alert alert-danger py-2">{error}</div>}

                            {cand && (
                                <React.Fragment>
                                    <hr />
                                    <BlueprintGateResults cand={cand} />

                                    {cand.ok && (
                                        <React.Fragment>
                                            <hr />
                                            <div className="row">
                                                <div className="col-md-8 mb-2">
                                                    <label htmlFor="refName">Nome (para salvar como novo)</label>
                                                    <input className="form-control form-control-sm" id="refName" value={name}
                                                        onChange={e => setName(e.target.value)} />
                                                </div>
                                                <div className="col-md-4 mb-2">
                                                    <label htmlFor="refDiff">Dificuldade</label>
                                                    <select className="form-select form-select-sm" id="refDiff" value={difficulty}
                                                        onChange={e => setDifficulty(e.target.value)}>
                                                        {DIFFICULTIES.map(d => <option key={d} value={d}>{d}</option>)}
                                                    </select>
                                                    <small className="text-muted d-block mt-1">Só um rótulo de complexidade que o trader vê na galeria — <strong>não muda nada na execução</strong>.</small>
                                                </div>
                                            </div>
                                        </React.Fragment>
                                    )}
                                </React.Fragment>
                            )}
                        </React.Fragment>
                        )}

                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Fechar</button>
                        {!isSystem && (
                            <button type="button" className="btn btn-outline-success" onClick={onUpdateInPlace}
                                disabled={saving || !cand || !cand.ok}>
                                {saving ? 'Salvando…' : 'Atualizar este template'}
                            </button>
                        )}
                        <button type="button" className="btn btn-success" onClick={onSaveAsNew}
                            disabled={saving || !cand || !cand.ok || !name.trim()}>
                            {saving ? 'Salvando…' : 'Salvar como novo'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default RefineStrategyModal;
